import {
  clearIntakeSession,
  createIntakeClaims,
  createIntakeSession,
  deleteIntakeSession,
  downloadIntakeFiles,
  fetchIntakeStatus,
  forgetIntakeTabSession,
  isIntakeSessionExpired,
  loadDeletingIntakeSessions,
  loadIntakeSessions,
  loadIntakeTabSession,
  markIntakeSessionDeleting,
  rememberIntakeTabSession,
  saveIntakeSession,
  type IntakeClaims,
  type IntakeSession,
} from '../intake-client';
import type { IntakeApi } from './intake-api';

/** When deletes that did not reach the server are worth sending again. */
export interface IntakeRetryTriggers {
  /** Calls the listener whenever the connection may be back. Returns an unsubscribe function. */
  subscribe(listener: () => void): () => void;
}

export interface BrowserIntakeOptions {
  /** Resume storage shared by the tabs; localStorage by default, null for none. */
  readonly storage?: Storage | null;
  /** This tab's own memory of the address it uses (survives reloads of the tab); sessionStorage by default, null for none. */
  readonly tabStorage?: Storage | null;
  /** Which page holds which address; Web Locks by default. */
  readonly claims?: IntakeClaims;
  /** When to send unconfirmed deletes again; by default browserRetryTriggers() (online, page shown again, every 30 s while visible). */
  readonly retryTriggers?: IntakeRetryTriggers | null;
}

/**
 * How often a visible page sends unconfirmed deletes again. A weak signal can
 * come back without an 'online' event (the browser counted as online all along).
 */
export const INTAKE_DELETE_RETRY_MS = 30_000;

/** The browser going back online, this page becoming visible again, and every INTAKE_DELETE_RETRY_MS while it is visible. */
export function browserRetryTriggers(): IntakeRetryTriggers | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  return {
    subscribe(listener) {
      const whenVisible = () => {
        if (document.visibilityState !== 'hidden') listener();
      };
      window.addEventListener('online', listener);
      document.addEventListener('visibilitychange', whenVisible);
      const timer = window.setInterval(whenVisible, INTAKE_DELETE_RETRY_MS);
      return () => {
        window.removeEventListener('online', listener);
        document.removeEventListener('visibilitychange', whenVisible);
        window.clearInterval(timer);
      };
    },
  };
}

/**
 * The real e-mail intake client (spec §7) behind the IntakeApi seam. Each
 * address has its own resume entry and is held by one open page at a time.
 * A deleted address keeps its entry (marked, never resumed) until the server
 * confirms the DELETE, which is sent again until it does.
 */
export function createBrowserIntakeApi(options: BrowserIntakeOptions = {}): IntakeApi {
  const { storage, tabStorage } = options;
  const claims = options.claims ?? createIntakeClaims();
  /** Deletes this page sent that the server has not confirmed, also when storage is blocked. */
  const unconfirmed = new Map<string, IntakeSession>();
  /** The DELETE in flight for each address: this page never sends two at once. */
  const inFlight = new Map<string, Promise<boolean>>();

  /** This page stops using the address (another tab may take it, or nobody). */
  function letGo(id: string): void {
    claims.release(id);
    forgetIntakeTabSession(id, tabStorage);
  }

  /** Resolves true once the server answered 204 or 404; only then is the entry (and its secret) removed. */
  function sendDelete(session: IntakeSession): Promise<boolean> {
    const running = inFlight.get(session.id);
    if (running) return running;
    // keepalive: the request still goes out when the person closes the tab right away.
    const attempt = deleteIntakeSession(session, { keepalive: true }).then(
      () => {
        unconfirmed.delete(session.id);
        clearIntakeSession(session, storage);
        return true;
      },
      () => false,
    );
    inFlight.set(session.id, attempt);
    void attempt.then(() => inFlight.delete(session.id));
    return attempt;
  }

  /** Sends every unconfirmed delete again: this page's own and those left by earlier pages or other tabs. */
  function retryDeletes(): void {
    const now = Date.now();
    const due = new Map<string, IntakeSession>();
    for (const [id, session] of unconfirmed) {
      // Past its deadline the server has dropped the address by itself.
      if (isIntakeSessionExpired(session, now)) unconfirmed.delete(id);
      else due.set(id, session);
    }
    for (const session of loadDeletingIntakeSessions(storage, now)) due.set(session.id, session);
    for (const session of due.values()) void sendDelete(session);
  }

  (options.retryTriggers === undefined ? browserRetryTriggers() : options.retryTriggers)?.subscribe(retryDeletes);

  return {
    create: async (signal) => {
      const session = await createIntakeSession(signal);
      await claims.take(session.id);
      // The server answers again, so earlier deletes may get through now.
      retryDeletes();
      return session;
    },
    status: (session, signal) => fetchIntakeStatus(session, signal),
    download: async (session, status, signal) => [...await downloadIntakeFiles(session, status, signal)],
    discard: (session) => {
      letGo(session.id);
      unconfirmed.set(session.id, session);
      // Storage full or blocked: never leave a resumable entry behind (this page still sends the delete again).
      if (!markIntakeSessionDeleting(session, storage)) clearIntakeSession(session, storage);
      return sendDelete(session);
    },
    save: (session) => {
      rememberIntakeTabSession(session.id, tabStorage);
      try {
        return saveIntakeSession(session, storage);
      } catch {
        // Resuming is a convenience; storage may be blocked.
        return false;
      }
    },
    load: () => {
      retryDeletes();
      let sessions: IntakeSession[] = [];
      try {
        sessions = loadIntakeSessions(storage);
      } catch {
        sessions = [];
      }
      // Never one this page is deleting, even if its entry could not be marked.
      sessions = sessions.filter((candidate) => !unconfirmed.has(candidate.id));
      // An address another page took over from this one stays with that page.
      const usable = sessions.filter((candidate) => !claims.isLost(candidate.id));
      // A reloaded tab goes back to its own address rather than the newest one, which another tab may use.
      const own = loadIntakeTabSession(tabStorage);
      if (own !== null && !sessions.some((candidate) => candidate.id === own)) forgetIntakeTabSession(own, tabStorage);
      const session = usable.find((candidate) => candidate.id === own) ?? usable[0] ?? null;
      return { session, elsewhere: session ? null : sessions[0] ?? null };
    },
    take: (session) => {
      rememberIntakeTabSession(session.id, tabStorage);
      return claims.take(session.id);
    },
    onLost: (listener) => claims.onLost(listener),
    clear: (session) => {
      letGo(session.id);
      unconfirmed.delete(session.id);
      try {
        clearIntakeSession(session, storage);
      } catch {
        // Ignore blocked storage.
      }
    },
  };
}
